import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { useLanguage } from '../../../helpers/language-context';

export default function Management() {
  const [managementTeam, setManagementTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const { translations, language } = useLanguage();

  useEffect(() => {
    const fetchManagement = async () => {
      const { data, error } = await supabase
        .from('management_team')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching management team:', error);
      } else {
        setManagementTeam(data);
      }
      setLoading(false);
    };

    fetchManagement();
  }, []);

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  // Helper to render members in order
  const renderMembers = () => {
    if (managementTeam.length === 0) return null;

    const mainMember = managementTeam[0];
    const level2Members = managementTeam.slice(1, 3);
    const level3Members = managementTeam.slice(3);

    return (
      <div className="flex flex-col items-center gap-16">

        {/* Top Level: Main Member (Position 1) */}
        {mainMember && (
          <div className="w-full max-w-sm text-center group">
            <div className="aspect-w-1 aspect-h-1 mb-6">
              <img
                src={mainMember.image ?? '/assets/images/placeholder-avatar.jpg'}
                alt={mainMember.name}
                className="w-full aspect-square object-cover object-top rounded-xl border-2 border-transparent group-hover:border-[#FDA729] transition-all duration-300 mx-auto"
                onError={(e) => { e.target.src = '/assets/images/placeholder-avatar.jpg'; }}
              />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-gray-800 group-hover:text-[#FDA729] transition-colors duration-300">
              {mainMember.name}
            </h3>
            <p className="text-base md:text-lg text-gray-600 mt-2">
              {language === 'en' ? mainMember['position-en'] : mainMember['position-ms']}
            </p>
          </div>
        )}

        {/* Second Level: 2 Members */}
        {level2Members.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-xl">
            {level2Members.map(member => (
              <div key={member.id} className="text-center group">
                <div className="aspect-w-1 aspect-h-1 mb-4">
                  <img
                    src={member.image ?? '/assets/images/placeholder-avatar.jpg'}
                    alt={member.name}
                    className="w-full aspect-square object-cover object-top rounded-xl border-2 border-transparent group-hover:border-[#FDA729] transition-all duration-300 mx-auto"
                    onError={(e) => { e.target.src = '/assets/images/placeholder-avatar.jpg'; }}
                  />
                </div>
                <h3 className="text-lg md:text-lg font-semibold text-gray-800 group-hover:text-[#FDA729] transition-colors duration-300">
                  {member.name}
                </h3>
                <p className="text-sm md:text-sm text-gray-600 mt-1">
                  {language === 'en' ? member['position-en'] : member['position-ms']}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Third Level: Rest of the members (5 per row) */}
        {level3Members.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 w-full">
            {level3Members.map(member => (
              <div key={member.id} className="text-center group">
                <div className="aspect-w-1 aspect-h-1 mb-4">
                  <img
                    src={member.image ?? '/assets/images/placeholder-avatar.jpg'}
                    alt={member.name}
                    className="w-full aspect-square object-cover object-top rounded-xl border-2 border-transparent group-hover:border-[#FDA729] transition-all duration-300 mx-auto"
                    onError={(e) => { e.target.src = '/assets/images/placeholder-avatar.jpg'; }}
                  />
                </div>
                <h3 className="text-xl md:text-lg font-semibold text-gray-800 group-hover:text-[#FDA729] transition-colors duration-300">
                  {member.name}
                </h3>
                <p className="text-base md:text-xs text-gray-600 mt-1">
                  {language === 'en' ? member['position-en'] : member['position-ms']}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="bg-white py-12">
      <div>
        <div className="flex items-center justify-between mb-16">
          <h2 className="text-3xl font-bold text-gray-800">{translations.about.managementTeam}</h2>
        </div>

        {renderMembers()}
      </div>
    </section>
  );
}
